import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getMigration, getMigrationSites, updateMigrationSite, createReport, createMigrationSite, deleteMigrationSite, bulkCreateSites, formatApiErrorDetail } from "../lib/api";
import { StepContent } from "../components/StepContent";
import { toast } from "sonner";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  Cloud, ChevronRight, Copy, Check, Shield, ListChecks, Mail, MessageSquareQuote,
  Network, MapPin, Server, GitBranch, Bookmark, ImagePlus, Share2, DatabaseBackup,
  RotateCcw, Pencil, FileDown, X, ShieldAlert, Voicemail, MessagesSquare, Plus, Trash2,
} from "lucide-react";

const CopyBtn = ({ text, label = "Copy", testId }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={copy} data-testid={testId}
      className="flex items-center gap-1.5 text-[11px] text-zinc-400 hover:text-white transition-colors">
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : label}
    </button>
  );
};

const SITE_STATUS = {
  pending: { label: "Pending", cls: "bg-zinc-700 text-zinc-200" },
  onboarded: { label: "Onboarded", cls: "bg-blue-600 text-white" },
  cutover: { label: "Cutover", cls: "bg-emerald-600 text-white" },
};

const ComponentTrack = ({ track, sectionTestId, stepPrefix, Icon, done, onToggle }) => {
  if (!track) return null;
  const total = track.steps.length;
  const doneCount = track.steps.filter((s) => done.has(s.id)).length;
  const pct = Math.round((doneCount / total) * 100);
  return (
    <div className="mt-8" data-testid={sectionTestId}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-blue-400" />} {track.title}
        </div>
        <span className="text-[11px] font-mono text-zinc-400" data-testid={`${sectionTestId}-count`}>{doneCount}/{total} · {pct}%</span>
      </div>
      <p className="text-sm text-zinc-400 mb-3 max-w-2xl">{track.summary}</p>
      <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden mb-3">
        <div className="h-full bg-emerald-500 transition-[width] duration-300" style={{ width: `${pct}%` }} data-testid={`${sectionTestId}-progress`} />
      </div>
      <Accordion type="single" collapsible className="space-y-3">
        {track.steps.map((step, idx) => (
          <AccordionItem key={step.id} value={step.id} data-testid={`${stepPrefix}-${step.id}`}
            className="border border-zinc-800 rounded-lg bg-zinc-900 overflow-hidden px-0">
            <AccordionTrigger className="px-5 hover:no-underline hover:bg-zinc-800/40 [&[data-state=open]]:bg-zinc-800/30">
              <div className="flex items-center gap-4 text-left">
                <span role="button" tabIndex={0} data-testid={`${stepPrefix}-done-${step.id}`}
                  onClick={(e) => { e.stopPropagation(); onToggle(step.id); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); onToggle(step.id); } }}
                  className={`h-8 w-8 shrink-0 rounded-md font-mono text-sm font-bold flex items-center justify-center border cursor-pointer transition-colors ${done.has(step.id) ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" : "bg-blue-600/15 border-blue-600/30 text-blue-400 hover:border-emerald-500/40"}`}
                  title={done.has(step.id) ? "Done — click to undo" : "Mark done"}>
                  {done.has(step.id) ? <Check className="h-4 w-4" /> : idx + 1}
                </span>
                <div>
                  <div className="font-heading font-bold text-white text-base">{step.title}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5 font-semibold">{step.layer}{done.has(step.id) ? " · done" : ""}</div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5"><StepContent step={step} /></AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

const SITE_COMPONENTS = [
  { key: "jabber", label: "Jabber" },
  { key: "voicemail", label: "Voicemail" },
  { key: "imp", label: "IM&P" },
  { key: "expressway", label: "Expressway" },
];

export default function Migration() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState("");
  const [done, setDone] = useState(new Set());
  const [compDone, setCompDone] = useState(new Set());
  const [path, setPath] = useState("lgw");
  const [sites, setSites] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [siteName, setSiteName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [editSite, setEditSite] = useState(null);
  const [siteForm, setSiteForm] = useState({ carrier: "", circuit_ids: "", foc_date: "", notes: "" });
  const navigate = useNavigate();

  useEffect(() => {
    getMigration().then((d) => { setData(d); setOpen(d.phases?.[0]?.id || ""); }).catch(() => {});
    loadSites();
  }, []);

  const loadSites = () => getMigrationSites().then(setSites).catch(() => {});

  const addSite = async () => {
    const name = window.prompt("New location name (e.g. 'Branch — Manchester'):", "");
    if (!name || !name.trim()) return;
    try { await createMigrationSite({ name: name.trim() }); toast.success("Location added"); loadSites(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add location"); }
  };

  const removeSite = async (site) => {
    if (!window.confirm(`Delete "${site.name}"? This also removes its runbook progress and gates.`)) return;
    try { await deleteMigrationSite(site.id); toast.success("Location deleted"); loadSites(); }
    catch { toast.error("Could not delete location"); }
  };

  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const bulkFileRef = useRef(null);

  const parseBulk = (text) => {
    const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return [];
    const splitRow = (l) => l.split(/[,\t]/).map((p) => p.trim().replace(/^"|"$/g, ""));
    const first = splitRow(lines[0]).map((h) => h.toLowerCase());
    const hasHeader = first.includes("name") || first.includes("site") || first.includes("location");
    const colKey = (h) => {
      if (["name", "site", "location"].includes(h)) return "name";
      if (["carrier", "provider"].includes(h)) return "carrier";
      if (["circuits", "circuit", "circuit_ids", "circuit ids", "circuit id"].includes(h)) return "circuit_ids";
      if (["users", "user count", "seats"].includes(h)) return "users";
      if (["wave", "migration wave"].includes(h)) return "wave";
      if (["foc", "foc_date", "foc date"].includes(h)) return "foc_date";
      if (["status"].includes(h)) return "status";
      return null;
    };
    const map = hasHeader ? first.map(colKey) : ["name", "users"];
    const rows = (hasHeader ? lines.slice(1) : lines).map((line) => {
      const cells = splitRow(line);
      const row = {};
      cells.forEach((c, i) => { const k = map[i]; if (k && c) row[k] = c; });
      if (!hasHeader && cells[0]) row.name = cells[0];
      return row.name ? row : null;
    }).filter(Boolean);
    return rows;
  };

  const doBulkImport = async () => {
    const rows = parseBulk(bulkText);
    if (!rows.length) { toast.error("Paste rows or upload a CSV with at least a name column"); return; }
    try {
      const res = await bulkCreateSites(rows);
      toast.success(`Imported ${res.created} location(s)`);
      setShowBulk(false); setBulkText(""); loadSites();
    } catch { toast.error("Import failed"); }
  };

  const onBulkFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setBulkText(String(reader.result || ""));
    reader.readAsText(file);
    e.target.value = "";
  };

  const toggleDone = (id) => setDone((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleComp = (id) => setCompDone((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const setSiteComponent = async (site, key) => {
    const current = (site.components || {})[key] || false;
    const nextComponents = { ...(site.components || {}), [key]: !current };
    setSites((prev) => prev.map((s) => (s.id === site.id ? { ...s, components: nextComponents } : s)));
    try { await updateMigrationSite(site.id, { components: nextComponents }); }
    catch {
      toast.error("Failed to update site components");
      setSites((prev) => prev.map((s) => (s.id === site.id ? { ...s, components: site.components } : s)));
    }
  };

  const setSiteStatus = async (site, status) => {
    const prevStatus = site.status;
    setSites((prev) => prev.map((s) => (s.id === site.id ? { ...s, status } : s)));
    try { await updateMigrationSite(site.id, { status }); }
    catch {
      toast.error("Failed to update site");
      setSites((prev) => prev.map((s) => (s.id === site.id ? { ...s, status: prevStatus } : s)));
    }
  };

  const openEdit = (site) => {
    setEditSite(site.id);
    setSiteForm({
      carrier: site.carrier || "",
      circuit_ids: site.circuit_ids || "",
      foc_date: site.foc_date || "",
      notes: site.notes || "",
      users: site.users ?? "",
      wave: site.wave || "",
    });
  };

  const saveSiteDetails = async (site) => {
    const payload = { ...siteForm, users: Number(siteForm.users) || 0 };
    setSites((prev) => prev.map((s) => (s.id === site.id ? { ...s, ...payload } : s)));
    setEditSite(null);
    try { await updateMigrationSite(site.id, payload); toast.success(`${site.name} updated`); }
    catch { toast.error("Failed to save site details"); }
  };

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("Logo must be under 500KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setLogoUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const saveReport = async () => {
    setSaving(true);
    try {
      const r = await createReport({
        kind: "migration",
        completed_steps: Array.from(done),
        notes: notes.trim() || null,
        site_name: siteName.trim() || null,
        logo_url: logoUrl || null,
      });
      setSaved(r);
      toast.success("Migration report saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const copyShare = () => {
    if (!saved) return;
    navigator.clipboard.writeText(`${window.location.origin}/report/${saved.share_id}`);
    toast.success("Share link copied");
  };

  if (!data) return <div className="p-5 sm:p-8 text-zinc-500 font-mono text-sm">Loading migration playbook…</div>;

  const scopeChips = [
    { icon: Server, label: `CUCM ${data.scope.cucm_version}` },
    { icon: MapPin, label: `${data.scope.sites} sites` },
    { icon: GitBranch, label: data.scope.pstn_strategy },
    { icon: Network, label: data.scope.discovery },
  ];
  const progress = Math.round((done.size / data.phases.length) * 100);
  const activePath = data.pstn_paths.find((p) => p.id === path) || data.pstn_paths[0];
  const cutoverCount = sites.filter((s) => s.status === "cutover").length;

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10 gap-3">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2 truncate">
          <span className="text-zinc-600">~/</span> migration <ChevronRight className="h-3 w-3" /> cucm to webex cloud
        </span>
        <button onClick={() => navigate("/migration/export")} data-testid="migration-export-btn"
          className="ml-auto flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors shrink-0">
          <FileDown className="h-4 w-4" /> Export All
        </button>
        <button onClick={() => { setSaved(null); setSaveOpen(true); }} data-testid="migration-save-report-btn"
          className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors shrink-0">
          <Bookmark className="h-4 w-4" /> Save Report
        </button>
      </header>

      <div className="p-5 sm:p-8 max-w-4xl">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
          <Cloud className="h-4 w-4" /> Cloud Migration Playbook
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">{data.title}</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">{data.summary}</p>

        <div className="flex flex-wrap gap-2 mt-5">
          {scopeChips.map((c, i) => (
            <span key={i} className="flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1 rounded-md border border-zinc-700 bg-zinc-950 text-zinc-300">
              <c.icon className="h-3.5 w-3.5 text-blue-400" /> {c.label}
            </span>
          ))}
        </div>

        {/* Per-site tracker */}
        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-5" data-testid="site-tracker">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
              <MapPin className="h-4 w-4 text-blue-400" /> Per-Site Migration Tracker
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-mono text-zinc-400">{cutoverCount}/{sites.length} cutover</span>
              <button onClick={addSite} data-testid="add-site-btn" className="no-print flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors">
                <Plus className="h-3.5 w-3.5" /> Add Site
              </button>
              <button onClick={() => setShowBulk(true)} data-testid="bulk-import-btn" className="no-print flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors">
                <FileDown className="h-3.5 w-3.5" /> Bulk import
              </button>
            </div>
          </div>

          <Dialog open={showBulk} onOpenChange={setShowBulk}>
            <DialogContent className="bg-zinc-900 border-zinc-800" data-testid="bulk-import-dialog">
              <DialogHeader>
                <DialogTitle className="text-white">Bulk import locations</DialogTitle>
                <DialogDescription className="text-zinc-400">
                  Upload a CSV or paste rows. With a header row you can include columns: <span className="font-mono text-zinc-300">name, carrier, circuits, users, wave, status</span>. Without a header, use <span className="font-mono text-zinc-300">Name, Users</span> per line.
                </DialogDescription>
              </DialogHeader>
              <div className="flex items-center gap-2">
                <input ref={bulkFileRef} type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={onBulkFile} data-testid="bulk-csv-file-input" />
                <button onClick={() => bulkFileRef.current?.click()} data-testid="bulk-upload-csv-btn" className="flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                  <FileDown className="h-3.5 w-3.5" /> Upload CSV file
                </button>
                <span className="text-[11px] text-zinc-600">or paste below</span>
              </div>
              <Textarea
                data-testid="bulk-import-textarea"
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                rows={8}
                placeholder={"name,carrier,circuits,users,wave\nHQ — London,BT,CID-1001,120,Wave 1 — Pilot\nBranch — Leeds,Gamma,CID-1002,30,Wave 2"}
                className="bg-zinc-950 border-zinc-800 text-sm font-mono"
              />
              <DialogFooter>
                <button onClick={() => setShowBulk(false)} className="text-[12px] px-3.5 py-2 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800">Cancel</button>
                <button onClick={doBulkImport} data-testid="bulk-import-submit" className="text-[12px] font-semibold px-3.5 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white">Import locations</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {sites.length === 0 && (
            <div data-testid="sites-empty" className="rounded-md border border-dashed border-zinc-700 bg-zinc-950 p-6 text-center">
              <div className="text-[13px] text-zinc-400">No locations yet. Add your first site to begin building its runbook.</div>
              <button onClick={addSite} data-testid="add-first-site-btn" className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold px-3.5 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="h-4 w-4" /> Add your first location
              </button>
            </div>
          )}
          <div className="space-y-2">
            {sites.map((site) => (
              <div key={site.id} data-testid={`site-row-${site.order}`} className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white font-medium">{site.name}</span>
                    {site.wave && <span data-testid={`site-${site.order}-wave-badge`} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border border-blue-500/40 bg-blue-500/10 text-blue-300">{site.wave}</span>}
                    <button onClick={() => (editSite === site.id ? setEditSite(null) : openEdit(site))} data-testid={`site-${site.order}-edit`}
                      className="text-zinc-500 hover:text-blue-400 transition-colors" title="Edit site details">
                      {editSite === site.id ? <X className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                    </button>
                    <button onClick={() => removeSite(site)} data-testid={`site-${site.order}-delete`}
                      className="text-zinc-500 hover:text-red-400 transition-colors" title="Delete site">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {["pending", "onboarded", "cutover"].map((st) => (
                      <button key={st} onClick={() => setSiteStatus(site, st)} data-testid={`site-${site.order}-${st}`}
                        className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors ${site.status === st ? SITE_STATUS[st].cls : "bg-transparent text-zinc-500 hover:text-zinc-300 border border-zinc-700"}`}>
                        {SITE_STATUS[st].label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Component cutover matrix */}
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`site-${site.order}-components`}>
                  {SITE_COMPONENTS.map((c) => {
                    const on = (site.components || {})[c.key];
                    return (
                      <button key={c.key} onClick={() => setSiteComponent(site, c.key)} data-testid={`site-${site.order}-comp-${c.key}`}
                        className={`flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border transition-colors ${on ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" : "bg-transparent border-zinc-700 text-zinc-500 hover:text-zinc-300"}`}>
                        {on && <Check className="h-3 w-3" />} {c.label}
                      </button>
                    );
                  })}
                </div>

                {/* Runbook summary (read-only) */}
                {editSite !== site.id && (site.carrier || site.circuit_ids || site.foc_date || site.users > 0) && (
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-[12px] text-zinc-400">
                    {site.carrier && <span><span className="text-zinc-600">Carrier:</span> {site.carrier}</span>}
                    {site.circuit_ids && <span><span className="text-zinc-600">Circuits:</span> <span className="font-mono">{site.circuit_ids}</span></span>}
                    {site.foc_date && <span><span className="text-zinc-600">FOC:</span> {site.foc_date}</span>}
                    {site.users > 0 && <span><span className="text-zinc-600">Users:</span> {site.users}</span>}
                  </div>
                )}
                {editSite !== site.id && site.notes && (
                  <p className="text-[12px] text-zinc-500 mt-1">{site.notes}</p>
                )}

                {/* Edit form */}
                {editSite === site.id && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid={`site-${site.order}-form`}>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Carrier</label>
                      <Input data-testid={`site-${site.order}-carrier`} value={siteForm.carrier} onChange={(e) => setSiteForm({ ...siteForm, carrier: e.target.value })} placeholder="e.g. AT&T" className="mt-1 h-9 bg-zinc-900 border-zinc-800 text-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Circuit ID(s)</label>
                      <Input data-testid={`site-${site.order}-circuit`} value={siteForm.circuit_ids} onChange={(e) => setSiteForm({ ...siteForm, circuit_ids: e.target.value })} placeholder="e.g. PRI-8841 / 77.HCGS…" className="mt-1 h-9 bg-zinc-900 border-zinc-800 text-sm font-mono" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">FOC / Cutover Date</label>
                      <Input data-testid={`site-${site.order}-foc`} value={siteForm.foc_date} onChange={(e) => setSiteForm({ ...siteForm, foc_date: e.target.value })} placeholder="e.g. 2026-07-15" className="mt-1 h-9 bg-zinc-900 border-zinc-800 text-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Users at site</label>
                      <Input data-testid={`site-${site.order}-users`} type="number" value={siteForm.users} onChange={(e) => setSiteForm({ ...siteForm, users: e.target.value })} placeholder="e.g. 120" className="mt-1 h-9 bg-zinc-900 border-zinc-800 text-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Migration Wave</label>
                      <Input data-testid={`site-${site.order}-wave`} value={siteForm.wave} onChange={(e) => setSiteForm({ ...siteForm, wave: e.target.value })} placeholder="e.g. Wave 1 — Pilot" className="mt-1 h-9 bg-zinc-900 border-zinc-800 text-sm" />
                    </div>
                    <div className="sm:col-span-3">
                      <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Notes</label>
                      <Input data-testid={`site-${site.order}-notes`} value={siteForm.notes} onChange={(e) => setSiteForm({ ...siteForm, notes: e.target.value })} placeholder="e.g. LOA signed; port order submitted" className="mt-1 h-9 bg-zinc-900 border-zinc-800 text-sm" />
                    </div>
                    <div className="sm:col-span-3 flex justify-end">
                      <button onClick={() => saveSiteDetails(site)} data-testid={`site-${site.order}-save`}
                        className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-1.5 rounded-md transition-colors">
                        <Check className="h-3.5 w-3.5" /> Save site
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Architecture */}
        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Target Architecture</div>
            <CopyBtn text={data.architecture} testId="copy-architecture" />
          </div>
          <pre className="rounded-md border border-zinc-800 bg-black p-4 overflow-x-auto">
            <code className="font-mono text-[12px] leading-relaxed text-emerald-300 whitespace-pre">{data.architecture}</code>
          </pre>
        </div>

        {/* DRS backup reminder */}
        {data.drs_backup && (
          <div className="mt-6 rounded-lg border border-amber-500/40 bg-amber-500/[0.04] p-5" data-testid="drs-backup">
            <div className="flex items-center gap-2 text-amber-400 text-[11px] uppercase tracking-widest font-semibold">
              <DatabaseBackup className="h-4 w-4" /> Do this first — {data.drs_backup.title}
            </div>
            <div className="mt-3">
              <StepContent step={data.drs_backup} />
            </div>
          </div>
        )}

        {/* DRS restore (rollback) reminder */}
        {data.drs_restore && (
          <div className="mt-4 rounded-lg border border-zinc-700 bg-zinc-900 p-5" data-testid="drs-restore">
            <div className="flex items-center gap-2 text-zinc-300 text-[11px] uppercase tracking-widest font-semibold">
              <RotateCcw className="h-4 w-4 text-blue-400" /> {data.drs_restore.title}
            </div>
            <div className="mt-3">
              <StepContent step={data.drs_restore} />
            </div>
          </div>
        )}

        {/* Phases with checklist */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
              <ListChecks className="h-4 w-4" /> Migration Phases ({data.phases.length})
            </div>
            <span className="text-[11px] font-mono text-zinc-400">{done.size}/{data.phases.length} · {progress}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden mb-3">
            <div className="h-full bg-blue-500 transition-[width] duration-300" style={{ width: `${progress}%` }} data-testid="mig-progress" />
          </div>
          <Accordion type="single" collapsible value={open} onValueChange={setOpen} className="space-y-3">
            {data.phases.map((step, idx) => (
              <AccordionItem key={step.id} value={step.id} data-testid={`mig-phase-${step.id}`}
                className="border border-zinc-800 rounded-lg bg-zinc-900 overflow-hidden px-0">
                <AccordionTrigger className="px-5 hover:no-underline hover:bg-zinc-800/40 [&[data-state=open]]:bg-zinc-800/30">
                  <div className="flex items-center gap-4 text-left">
                    <span role="button" tabIndex={0} data-testid={`mig-done-${step.id}`}
                      onClick={(e) => { e.stopPropagation(); toggleDone(step.id); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); toggleDone(step.id); } }}
                      className={`h-8 w-8 shrink-0 rounded-md font-mono text-sm font-bold flex items-center justify-center border cursor-pointer transition-colors ${done.has(step.id) ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" : "bg-blue-600/15 border-blue-600/30 text-blue-400 hover:border-emerald-500/40"}`}
                      title={done.has(step.id) ? "Done — click to undo" : "Mark phase done"}>
                      {done.has(step.id) ? <Check className="h-4 w-4" /> : idx + 1}
                    </span>
                    <div>
                      <div className="font-heading font-bold text-white text-base">{step.title}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5 font-semibold">{step.layer}{done.has(step.id) ? " · done" : ""}</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-5">
                  <StepContent step={step} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Component migration tracks: Jabber, Unity, IM&P, Expressway */}
        <ComponentTrack track={data.jabber_migration} sectionTestId="jabber-migration" stepPrefix="jabber-step" Icon={MessageSquareQuote} done={compDone} onToggle={toggleComp} />
        <ComponentTrack track={data.unity_migration} sectionTestId="unity-migration" stepPrefix="unity-step" Icon={Voicemail} done={compDone} onToggle={toggleComp} />
        <ComponentTrack track={data.imp_migration} sectionTestId="imp-migration" stepPrefix="imp-step" Icon={MessagesSquare} done={compDone} onToggle={toggleComp} />
        <ComponentTrack track={data.expressway_migration} sectionTestId="expressway-migration" stepPrefix="expressway-step" Icon={Shield} done={compDone} onToggle={toggleComp} />

        {/* Contact Center note */}
        {data.contact_center_note && (
          <div className="mt-8 rounded-lg border border-amber-500/40 bg-amber-500/[0.04] p-5" data-testid="contact-center-note">
            <div className="flex items-center gap-2 text-amber-400 text-[11px] uppercase tracking-widest font-semibold">
              <ShieldAlert className="h-4 w-4" /> {data.contact_center_note.title}
            </div>
            <p className="text-sm text-zinc-300 mt-3">{data.contact_center_note.summary}</p>
            <ul className="mt-3 space-y-2">
              {data.contact_center_note.considerations.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-400">
                  <span className="text-amber-400 mt-0.5">▸</span> {c}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* PSTN paths */}
        <div className="mt-8">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3 flex items-center gap-2">
            <GitBranch className="h-4 w-4" /> PSTN Strategy
          </div>
          <div className="flex gap-2 mb-4 flex-wrap">
            {data.pstn_paths.map((p) => (
              <button key={p.id} onClick={() => setPath(p.id)} data-testid={`pstn-tab-${p.id}`}
                className={`text-sm font-medium px-4 py-2 rounded-md border transition-colors ${path === p.id ? "bg-blue-600/15 border-blue-600/40 text-blue-400" : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-white"}`}>
                {p.name}{p.recommended ? " ★" : ""}
              </button>
            ))}
          </div>
          <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-5" data-testid={`pstn-panel-${activePath.id}`}>
            <div className="text-sm text-zinc-300"><span className="text-zinc-500 font-semibold">When to use: </span>{activePath.when_to_use}</div>
            <div className="text-sm text-zinc-300 mt-1"><span className="text-zinc-500 font-semibold">Action: </span>{activePath.action}</div>
            <Accordion type="single" collapsible className="space-y-3 mt-4">
              {activePath.steps.map((step, idx) => (
                <AccordionItem key={step.id} value={step.id} data-testid={`pstn-step-${step.id}`}
                  className="border border-zinc-800 rounded-lg bg-zinc-950 overflow-hidden px-0">
                  <AccordionTrigger className="px-5 hover:no-underline hover:bg-zinc-800/40 [&[data-state=open]]:bg-zinc-800/30">
                    <div className="flex items-center gap-4 text-left">
                      <div className="h-7 w-7 shrink-0 rounded-md bg-blue-600/15 border border-blue-600/30 text-blue-400 font-mono text-xs font-bold flex items-center justify-center">{idx + 1}</div>
                      <div>
                        <div className="font-heading font-bold text-white text-sm">{step.title}</div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5 font-semibold">{step.layer}</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5"><StepContent step={step} /></AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        {/* Firewall */}
        <div className="mt-8 rounded-lg border border-zinc-800 bg-zinc-900 p-5">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3 flex items-center gap-2">
            <Shield className="h-4 w-4 text-amber-400" /> Firewall / Egress Requirements
          </div>
          <div className="space-y-2">
            {data.firewall.map((f, i) => (
              <div key={i} className="flex items-start gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3">
                <span className="font-mono text-[13px] text-emerald-300 shrink-0 min-w-[140px]">{f.proto}</span>
                <span className="text-sm text-zinc-400">{f.purpose}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Prerequisites */}
        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-5">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">Prerequisites / Next Steps</div>
          <ul className="space-y-2">
            {data.prerequisites.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                <Check className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" /> {p}
              </li>
            ))}
          </ul>
        </div>

        {/* Handoff */}
        <div className="mt-8">
          <div className="text-[11px] uppercase tracking-widest text-blue-400 font-semibold mb-3">Handoff Package</div>
          <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-white"><MessageSquareQuote className="h-4 w-4 text-blue-400" /> Kickoff Meeting Script</div>
              <CopyBtn text={data.handoff.kickoff_script} testId="copy-kickoff" />
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed italic">"{data.handoff.kickoff_script}"</p>
          </div>
          <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-white"><Mail className="h-4 w-4 text-blue-400" /> Formal Email</div>
              <CopyBtn text={`Subject: ${data.handoff.email_subject}\n\n${data.handoff.email_body}`} label="Copy email" testId="copy-email" />
            </div>
            <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Subject</div>
              <div className="text-sm text-zinc-200 mb-3">{data.handoff.email_subject}</div>
              <pre className="font-sans text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{data.handoff.email_body}</pre>
            </div>
          </div>
        </div>

        <div className="mt-8 rounded-lg border border-zinc-800 bg-zinc-900 p-5 flex items-center justify-between gap-4">
          <div className="text-sm text-zinc-400">Need to dig into a specific step (CUBE, LGW, porting, replication)?</div>
          <button onClick={() => navigate(`/assistant?context=${encodeURIComponent("CUCM to Webex Cloud migration")}`)}
            data-testid="migration-ask-assistant-btn"
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors shrink-0">
            <MessageSquareQuote className="h-4 w-4" /> Ask AI Assistant
          </button>
        </div>
      </div>

      {/* Save Report dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-h-[90vh] overflow-y-auto" data-testid="mig-save-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Save Migration Report</DialogTitle>
            <DialogDescription className="text-zinc-500">Bookmark this migration checklist as a shareable, printable report.</DialogDescription>
          </DialogHeader>
          {!saved ? (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">{done.size} of {data.phases.length} phases marked done.</p>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Site / Customer Name (optional)</label>
                <Input data-testid="mig-site-input" value={siteName} onChange={(e) => setSiteName(e.target.value)} placeholder="e.g. Acme Corp — Migration" className="mt-2 bg-zinc-950 border-zinc-800" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Customer Logo (optional)</label>
                <div className="mt-2 flex items-center gap-3">
                  <label className="cursor-pointer inline-flex items-center gap-2 px-3 h-10 rounded-md bg-zinc-800 hover:bg-zinc-700 text-sm font-medium transition-colors">
                    <ImagePlus className="h-4 w-4" /> {logoUrl ? "Change logo" : "Upload logo"}
                    <input type="file" accept="image/*" onChange={onLogoChange} data-testid="mig-logo-input" className="hidden" />
                  </label>
                  {logoUrl && <img src={logoUrl} alt="logo" className="h-10 w-10 object-contain rounded bg-white/5 border border-zinc-800" />}
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Notes (optional)</label>
                <Textarea data-testid="mig-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. FOC date confirmed for Sites 1-3; firewall change ticket #4821…" className="mt-2 bg-zinc-950 border-zinc-800 min-h-[90px]" />
              </div>
              <DialogFooter>
                <button onClick={() => setSaveOpen(false)} className="px-4 py-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-sm font-medium transition-colors">Cancel</button>
                <button onClick={saveReport} disabled={saving} data-testid="mig-save-confirm-btn" className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-sm font-medium transition-colors flex items-center gap-2">
                  <Bookmark className="h-4 w-4" /> {saving ? "Saving…" : "Save Report"}
                </button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4" data-testid="mig-saved-state">
              <div className="flex items-center gap-2 text-emerald-400 text-sm"><Check className="h-5 w-5" /> Migration report saved.</div>
              <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 font-mono text-xs text-zinc-300 break-all">
                {`${window.location.origin}/report/${saved.share_id}`}
              </div>
              <DialogFooter>
                <button onClick={copyShare} data-testid="mig-copy-share-btn" className="px-4 py-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-sm font-medium transition-colors flex items-center gap-2"><Share2 className="h-4 w-4" /> Copy link</button>
                <button onClick={() => navigate(`/report/${saved.share_id}`)} data-testid="mig-open-saved-btn" className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-sm font-medium transition-colors">Open report</button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
